import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Backdrop, CircularProgress, Dialog } from "@mui/material";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash2,
  Camera,
  ArrowLeft,
} from "feather-icons-react/build/IconComponents";
import API from "../../API/newIndex";
import Notification from "../Notification";
import {
  ButtonComponent,
  DetailsComponent,
  InputComponent,
  SelectComponent,
} from "@shopeasy-dashboard-ui/ui-library";
import TextareaComponent from "../FormInput/TextareaComponent";
import { getVendorValidationSchema, validateTabFields } from "./vendorValidation";

const slugify = (text) =>
  text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\w\-]+/g, "")
    .replace(/\-\-+/g, "-");

const getChangedFields = (currentValues, originalValues) => {
  if (!currentValues || !originalValues) return currentValues || {};
  const changed = {};
  Object.keys(currentValues).forEach((key) => {
    const cur = currentValues[key];
    const orig = originalValues[key];
    if (JSON.stringify(cur) !== JSON.stringify(orig)) {
      changed[key] = cur;
    }
  });
  return changed;
};

const defaultVendorFormValues = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  country_code: "+91",
  profile_picture: "",
  vendor_logo: "",
  vendor_banner: "",
  shop_name: "",
  shop_slug: "",
  vendor_tagline: "",
  vendor_address: "",
  commission_type: "percentage",
  commission: 0,
  social_links: {
    twitter: "",
    facebook: "",
    instagram: "",
    linkedin: "",
  },
};

const parseVendorToForm = (v) => {
  if (!v) return { ...defaultVendorFormValues };

  let socialData = v.social_links || v.social_app;
  if (typeof socialData === "string") {
    try {
      socialData = JSON.parse(socialData);
    } catch (e) {
      socialData = {};
    }
  }
  if (!socialData || typeof socialData !== "object") {
    socialData = {};
  }

  const logoValue = v.vendor_logo || v.logo || v.profile_picture || v.avatar || "";
  const bannerValue = v.vendor_banner || v.banner || "";

  let addressValue = v.vendor_address || v.business_address || "";
  if (typeof addressValue === "string") {
    try {
      const parsed = JSON.parse(addressValue);
      if (typeof parsed === "object" && parsed !== null) {
        const parts = [
          parsed.street,
          parsed.street2,
          parsed.city,
          parsed.state,
          parsed.zip,
          parsed.country,
        ].filter(Boolean);
        if (parts.length > 0) addressValue = parts.join(", ");
      }
    } catch (e) { }
  } else if (typeof addressValue === "object" && addressValue !== null) {
    const parts = [
      addressValue.street,
      addressValue.street2,
      addressValue.city,
      addressValue.state,
      addressValue.zip,
      addressValue.country,
    ].filter(Boolean);
    if (parts.length > 0) addressValue = parts.join(", ");
  }

  return {
    first_name: v.first_name || (v.name ? v.name.split(" ")[0] : ""),
    last_name: v.last_name || (v.name ? v.name.split(" ").slice(1).join(" ") : ""),
    email: v.email || "",
    phone: v.phone || "",
    country_code: v.country_code || "+91",
    profile_picture: logoValue,
    vendor_logo: logoValue,
    vendor_banner: bannerValue,
    shop_name: v.shop_name || "",
    shop_slug: v.shop_slug || slugify(v.shop_name || ""),
    vendor_tagline: v.vendor_tagline || v.tagline || "",
    vendor_address: addressValue,
    commission_type: v.commission_type || "percentage",
    commission: v.commission ?? v.commission_rate ?? 0,
    social_links: {
      twitter: socialData?.twitter || v.twitter || "",
      facebook: socialData?.facebook || v.facebook || "",
      instagram: socialData?.instagram || v.instagram || "",
      linkedin: socialData?.linkedin || v.linkedin || "",
    },
  };
};

const BulkEditVendor = ({
  isBulkEdit = true,
  bulkVendorIds = [],
  onClose,
  onSuccess,
}) => {
  const [bulkVendorsList, setBulkVendorsList] = useState([]);
  const [activeBulkIndex, setActiveBulkIndex] = useState(0);
  const [vendorSearchTerm, setVendorSearchTerm] = useState("");
  const bulkVendorsDataRef = useRef({});

  const [activeTab, setActiveTab] = useState("general");
  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);

  const [formValues, setFormValues] = useState(defaultVendorFormValues);
  const [formErrors, setFormErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    severity: "info",
  });

  const profileInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const originalDataRef = useRef(null);

  // Fetch details for all selected vendors using getdatabyids with fallback
  const fetchVendorsData = useCallback(async () => {
    if (!bulkVendorIds || bulkVendorIds.length === 0) return;
    setLoading(true);
    try {
      let loadedRecords = [];
      try {
        const res = await API({
          url: "/admin/vendor/getdatabyids",
          method: "post",
          data: { ids: bulkVendorIds, vendors: bulkVendorIds },
        });
        if (res?.status === 200 || res?.status === 304) {
          const resultData = res?.data;
          loadedRecords =
            resultData?.data?.records ||
            resultData?.records ||
            resultData?.data?.vendors ||
            (Array.isArray(resultData?.data) ? resultData.data : []) ||
            (Array.isArray(resultData) ? resultData : []);
        }
      } catch (err) {
        console.warn("getdatabyids failed, falling back to individual detail fetch:", err);
      }

      if (!loadedRecords || loadedRecords.length === 0) {
        // Fallback: fetch vendor details in parallel
        const detailPromises = bulkVendorIds.map(async (id) => {
          try {
            const res = await API({
              url: `/admin/vendor/detail/${id}`,
              method: "get",
            });
            return res?.data?.data || null;
          } catch (e) {
            console.error(`Failed to fetch detail for vendor ${id}:`, e);
            return null;
          }
        });

        const results = await Promise.all(detailPromises);
        loadedRecords = results.filter(Boolean);
      }

      if (loadedRecords.length > 0) {
        setBulkVendorsList(loadedRecords);
        const firstVendor = loadedRecords[0];
        const initialParsed = parseVendorToForm(firstVendor);
        originalDataRef.current = initialParsed;
        setFormValues(initialParsed);
      } else {
        setNotification({
          show: true,
          message: "No vendor records found for bulk edit",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching bulk vendor data:", error);
      setNotification({
        show: true,
        message: error?.message || "Failed to load vendor records",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [bulkVendorIds]);

  useEffect(() => {
    fetchVendorsData();
  }, [fetchVendorsData]);

  // Check if a vendor has unsaved changes
  const isVendorModified = useCallback(
    (vendor, index) => {
      if (!vendor) return false;
      if (index === activeBulkIndex) {
        const baseline = originalDataRef.current;
        if (!baseline) return false;
        const changed = getChangedFields(formValues, baseline);
        return Object.keys(changed).length > 0;
      }
      return Boolean(bulkVendorsDataRef.current[vendor._id]?.isModified);
    },
    [activeBulkIndex, formValues]
  );

  const modifiedCount = useMemo(() => {
    return bulkVendorsList.reduce((acc, vendor, idx) => {
      return acc + (isVendorModified(vendor, idx) ? 1 : 0);
    }, 0);
  }, [bulkVendorsList, isVendorModified]);

  // Switch between vendors in the sidebar
  const switchActiveVendor = (newIndex) => {
    if (newIndex < 0 || newIndex >= bulkVendorsList.length) return;
    if (newIndex === activeBulkIndex) return;

    const currentVendor = bulkVendorsList[activeBulkIndex];
    if (currentVendor) {
      const baseline = originalDataRef.current;
      const changed = baseline ? getChangedFields(formValues, baseline) : {};
      const isModified = Object.keys(changed).length > 0;

      bulkVendorsDataRef.current[currentVendor._id] = {
        values: { ...formValues },
        original: originalDataRef.current,
        isModified,
        errors: formErrors,
      };
    }

    setActiveBulkIndex(newIndex);
    const nextVendor = bulkVendorsList[newIndex];
    if (!nextVendor) return;

    const saved = bulkVendorsDataRef.current[nextVendor._id];
    if (saved) {
      originalDataRef.current = saved.original;
      setFormValues(saved.values);
      setFormErrors(saved.errors || {});
    } else {
      const parsed = parseVendorToForm(nextVendor);
      originalDataRef.current = parsed;
      setFormValues(parsed);
      setFormErrors({});
    }
    setTouchedFields({});
    setActiveTab("general");
  };

  const handleFieldChange = (name, value) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleShopNameChange = (e) => {
    const value = e.target.value;
    setFormValues((prev) => {
      const isSlugCustom = prev.shop_slug && prev.shop_slug !== slugify(prev.shop_name);
      return {
        ...prev,
        shop_name: value,
        shop_slug: isSlugCustom ? prev.shop_slug : slugify(value),
      };
    });
  };

  const handleNestedChange = (parentKey, childKey, value) => {
    setFormValues((prev) => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [childKey]: value,
      },
    }));
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    e.target.value = "";

    const maxMb = field === "vendor_banner" ? 5 : 2;
    const MAX_SIZE = maxMb * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setNotification({
        show: true,
        message: `${field === "vendor_banner" ? "Banner" : "Profile picture"} size must be less than ${maxMb}MB`,
        severity: "error",
      });
      return;
    }

    setUploadingField(field);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", "vendor");

      const res = await API({
        url: "/admin/fileupload/upload",
        method: "post",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      const uploadedUrl =
        (Array.isArray(res?.data?.data) ? res.data.data[0] : null) ||
        res?.data?.url ||
        (typeof res?.data?.data === "string" ? res.data.data : null) ||
        res?.data?.data?.url;

      if (uploadedUrl) {
        setFormValues((prev) => ({
          ...prev,
          [field]: uploadedUrl,
          ...(field === "vendor_logo" || field === "profile_picture"
            ? { vendor_logo: uploadedUrl, profile_picture: uploadedUrl }
            : {}),
        }));
        setNotification({
          show: true,
          message: "Image uploaded successfully",
          severity: "success",
        });
      }
    } catch (err) {
      console.error("Image upload failed:", err);
      setNotification({
        show: true,
        message: err?.response?.data?.message || err.message || "Failed to upload image",
        severity: "error",
      });
    } finally {
      setUploadingField(null);
    }
  };

  const handleTabClick = async (tab) => {
    if (tab === activeTab) return;
    const errors = await validateTabFields(activeTab, formValues, true);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setTouchedFields((prev) => ({ ...prev, ...errors }));
      return;
    }
    setActiveTab(tab);
  };

  const handleNext = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    let targetTab = "general";
    if (activeTab === "general") targetTab = "commission";
    else if (activeTab === "commission") targetTab = "social";

    const errors = await validateTabFields(activeTab, formValues, true);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setTouchedFields((prev) => ({ ...prev, ...errors }));
      return;
    }

    setActiveTab(targetTab);
  };

  const handlePrev = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (activeTab === "social") setActiveTab("commission");
    else if (activeTab === "commission") setActiveTab("general");
  };

  const handleBulkUpdate = async () => {
    // Flush current active vendor state into ref
    const currentVendor = bulkVendorsList[activeBulkIndex];
    if (currentVendor) {
      const baseline = originalDataRef.current;
      const changed = baseline ? getChangedFields(formValues, baseline) : {};
      const isModified = Object.keys(changed).length > 0;

      bulkVendorsDataRef.current[currentVendor._id] = {
        values: { ...formValues },
        original: originalDataRef.current,
        isModified,
      };
    }

    const modifiedEntries = Object.entries(bulkVendorsDataRef.current).filter(
      ([_, data]) => data?.isModified
    );

    if (modifiedEntries.length === 0) {
      setNotification({
        show: true,
        message: "No changes detected to save",
        severity: "info",
      });
      return;
    }

    // Validate all modified vendors
    const schema = getVendorValidationSchema(true);
    for (const [vendorId, data] of modifiedEntries) {
      try {
        await schema.validate(data.values, { abortEarly: false });
      } catch (validationErr) {
        const vendorObj = bulkVendorsList.find((v) => v._id === vendorId);
        const vendorIndex = bulkVendorsList.findIndex((v) => v._id === vendorId);
        if (vendorIndex !== -1) {
          switchActiveVendor(vendorIndex);
        }
        const firstErrorMsg =
          validationErr?.errors?.[0] || validationErr?.message || "Please fix validation errors";
        setNotification({
          show: true,
          message: `Validation error in ${vendorObj?.shop_name || "vendor"}: ${firstErrorMsg}`,
          severity: "error",
        });
        return;
      }
    }

    try {
      setBtnLoading(true);

      const bulkPayload = modifiedEntries.map(([vendorId, data]) => {
        const vals = data.values;
        return {
          _id: vendorId,
          id: vendorId,
          first_name: vals.first_name,
          last_name: vals.last_name,
          email: vals.email,
          phone: vals.phone,
          country_code: vals.country_code,
          profile_picture: vals.vendor_logo || vals.profile_picture || "",
          shop_name: vals.shop_name,
          shop_slug: vals.shop_slug || slugify(vals.shop_name),
          vendor_logo: vals.vendor_logo || vals.profile_picture || "",
          vendor_banner: vals.vendor_banner,
          vendor_tagline: vals.vendor_tagline,
          vendor_address: vals.vendor_address,
          commission_type: vals.commission_type,
          commission: Number(vals.commission) || 0,
          social_links: vals.social_links || {},
        };
      });

      let isSuccess = false;
      try {
        const res = await API({
          url: "/admin/vendor/bulk-update",
          method: "post",
          data: { vendors: bulkPayload, data: bulkPayload },
        });
        if (res?.status === 200 || res?.status === 304) {
          isSuccess = true;
        }
      } catch (bulkErr) {
        console.warn("bulk-update endpoint failed, trying direct array payload or individual updates:", bulkErr);
        try {
          const res = await API({
            url: "/admin/vendor/bulk-update",
            method: "post",
            data: bulkPayload,
          });
          if (res?.status === 200 || res?.status === 304) {
            isSuccess = true;
          }
        } catch (arrayErr) {
          // Fallback to updating individually in parallel
          const updatePromises = bulkPayload.map((payload) =>
            API({
              url: `/admin/vendor/update/${payload._id}`,
              method: "put",
              data: payload,
            })
          );
          await Promise.all(updatePromises);
          isSuccess = true;
        }
      }

      if (isSuccess) {
        bulkVendorsDataRef.current = {};
        if (onSuccess) {
          onSuccess(modifiedEntries.length);
        }
      }
    } catch (err) {
      console.error("Bulk update vendor error:", err);
      setNotification({
        show: true,
        message: err?.response?.data?.message || err.message || "Failed to update selected vendors",
        severity: "error",
      });
    } finally {
      setBtnLoading(false);
    }
  };

  const handleCancelClick = () => {
    if (modifiedCount > 0) {
      setShowUnsavedModal(true);
    } else {
      if (onClose) onClose();
    }
  };

  const filteredVendorsList = useMemo(() => {
    if (!vendorSearchTerm || !vendorSearchTerm.trim()) return bulkVendorsList;
    const term = vendorSearchTerm.toLowerCase();
    return bulkVendorsList.filter((v) => {
      const sName = (v?.shop_name || "").toLowerCase();
      const name = (v?.name || `${v?.first_name || ""} ${v?.last_name || ""}`).toLowerCase();
      const email = (v?.email || "").toLowerCase();
      const slug = (v?.shop_slug || "").toLowerCase();
      return sName.includes(term) || name.includes(term) || email.includes(term) || slug.includes(term);
    });
  }, [bulkVendorsList, vendorSearchTerm]);

  const activeVendor = bulkVendorsList[activeBulkIndex];
  const activeShopName = formValues.shop_name || activeVendor?.shop_name || activeVendor?.name || "Vendor";

  const tabClass = (isActive) =>
    `pb-2.5 px-3 text-xs sm:text-sm font-medium border-b-2 transition-colors duration-200 cursor-pointer ${isActive
      ? "border-[var(--btn-bg-primary)] text-[var(--primary-text-color)] font-semibold"
      : "border-transparent text-[var(--table-text-color)] hover:text-[var(--primary-text-color)]"
    }`;

  return (
    <div className="flex flex-col h-screen w-screen overflow-hidden bg-gray-100 dark:bg-zinc-950 text-gray-900 dark:text-zinc-100">
      {notification.show && (
        <Notification
          message={notification.message}
          close={() => setNotification((prev) => ({ ...prev, show: false }))}
          severity={notification.severity}
        />
      )}

      {loading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading}
          className="flex flex-col"
        >
          <CircularProgress color="inherit" />
          <p className="mt-2 text-sm font-medium">Loading vendor details...</p>
        </Backdrop>
      )}

      {/* Top Header Bar */}
      <div className="flex items-center justify-between px-6 py-3.5 border-b border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shrink-0">
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={handleCancelClick}
            className="w-7 h-7 rounded-full flex items-center justify-center text-gray-500 dark:text-zinc-400 hover:bg-gray-100 dark:hover:bg-zinc-800 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
            title="Close"
          >
            <X size={18} />
          </button>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <h5 className="text-sm font-bold text-gray-900 dark:text-white m-0">
                Bulk Edit Vendors
              </h5>
              <span className="bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-300 text-[11px] font-semibold px-2 py-0.5 rounded-full">
                {bulkVendorsList.length} Selected
              </span>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-zinc-400 m-0 mt-0.5">
              Select any vendor from the list to view and edit its details.
            </p>
          </div>
        </div>
      </div>

      {/* Main Body */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left Sidebar Panel */}
        <div className="w-[300px] border-r border-gray-200 dark:border-zinc-800 bg-gray-50 dark:bg-zinc-950 p-4 flex flex-col shrink-0">
          <div className="flex-1 flex flex-col border border-gray-200 dark:border-zinc-800 rounded-2xl bg-white dark:bg-zinc-900 overflow-hidden shadow-xs">
            {/* Header section */}
            <div className="p-3.5 border-b border-gray-200 dark:border-zinc-800 flex items-center justify-between">
              <span className="text-xs font-bold text-gray-900 dark:text-white">
                Selected Vendors
              </span>
              <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-zinc-400">
                <button
                  type="button"
                  disabled={activeBulkIndex === 0}
                  onClick={() => switchActiveVendor(activeBulkIndex - 1)}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 disabled:opacity-30 disabled:pointer-events-none cursor-pointer text-gray-600 dark:text-zinc-400"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="font-mono text-[11px] text-gray-600 dark:text-zinc-400">
                  {bulkVendorsList.length > 0 ? activeBulkIndex + 1 : 0}/{bulkVendorsList.length}
                </span>
                <button
                  type="button"
                  disabled={activeBulkIndex === bulkVendorsList.length - 1}
                  onClick={() => switchActiveVendor(activeBulkIndex + 1)}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 disabled:opacity-30 disabled:pointer-events-none cursor-pointer text-gray-600 dark:text-zinc-400"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>

            {/* Filter Search Input Section */}
            <div className="p-3 border-b border-gray-200 dark:border-zinc-800 bg-transparent">
              <input
                type="text"
                placeholder="Filter selected vendors..."
                value={vendorSearchTerm}
                onChange={(e) => setVendorSearchTerm(e.target.value)}
                className="w-full px-3 py-1.5 text-xs border border-gray-200 dark:border-zinc-700/80 rounded-lg bg-white dark:bg-zinc-800/80 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-zinc-400 focus:outline-none shadow-xs"
              />
            </div>

            {/* Items List */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {filteredVendorsList.map((vendor) => {
                const actualIndex = bulkVendorsList.findIndex((item) => item._id === vendor._id);
                const isActive = actualIndex === activeBulkIndex;
                const isModified = isVendorModified(vendor, actualIndex);
                const displayLogo =
                  actualIndex === activeBulkIndex
                    ? formValues.vendor_logo || formValues.profile_picture
                    : vendor?.vendor_logo || vendor?.profile_picture || vendor?.logo;
                const displayName =
                  actualIndex === activeBulkIndex
                    ? formValues.shop_name || formValues.first_name || vendor?.shop_name || vendor?.name
                    : vendor?.shop_name || vendor?.name;

                return (
                  <div
                    key={vendor._id}
                    onClick={() => switchActiveVendor(actualIndex)}
                    className={`p-3 cursor-pointer transition-colors flex items-center justify-between gap-3 border-b border-gray-200 dark:border-zinc-800 last:border-b-0 ${isActive
                      ? "bg-gray-100 dark:bg-zinc-800/90"
                      : "bg-white dark:bg-zinc-900 hover:bg-gray-100 dark:hover:bg-zinc-800/60"
                      }`}
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-lg bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700/80 flex items-center justify-center shrink-0 overflow-hidden font-bold text-xs text-gray-700 dark:text-zinc-200">
                        {displayLogo ? (
                          <img
                            src={displayLogo}
                            className="w-full h-full object-cover"
                            alt=""
                          />
                        ) : (
                          (displayName ? displayName[0] : "V").toUpperCase()
                        )}
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span
                          className={`text-xs truncate ${isActive
                            ? "font-bold text-gray-900 dark:text-white"
                            : "font-medium text-gray-800 dark:text-zinc-200"
                            }`}
                        >
                          {displayName || "Unnamed Vendor"}
                        </span>
                        <span className="text-[11px] text-gray-400 dark:text-zinc-400 truncate mt-0.5">
                          {vendor?.email || vendor?.shop_slug || "Vendor"}
                        </span>
                      </div>
                    </div>
                    {isModified && (
                      <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" title="Unsaved changes" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Form Area */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden bg-gray-100 dark:bg-zinc-950">
          {/* Right Sub-Header Bar */}
          <div className="flex items-center justify-between px-6 py-2.5 bg-white dark:bg-zinc-900 border-b border-gray-200 dark:border-zinc-800 text-xs shrink-0">
            <div>
              <span className="font-bold text-gray-900 dark:text-white">
                Currently Editing: {activeShopName}
              </span>{" "}
              <span className="text-gray-400 dark:text-zinc-500 font-mono text-[11px]">
                ({bulkVendorsList.length > 0 ? activeBulkIndex + 1 : 0} of {bulkVendorsList.length})
              </span>
            </div>
            <div>
              {modifiedCount > 0 ? (
                <span className="text-emerald-600 dark:text-emerald-400 font-medium text-xs">
                  {modifiedCount} vendor{modifiedCount > 1 ? "s" : ""} modified
                </span>
              ) : (
                <span className="text-gray-400 dark:text-zinc-500 text-xs font-normal">
                  No unsaved changes
                </span>
              )}
            </div>
          </div>

          {/* Scrollable Form Content */}
          <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
            <div className="max-w-4xl mx-auto flex flex-col gap-4">
              {/* Sub Navigation Tabs */}
              <div className="flex items-center gap-6 border-b main-border-color mb-2">
                <button
                  type="button"
                  onClick={() => handleTabClick("general")}
                  className={tabClass(activeTab === "general")}
                >
                  General Info
                </button>
                <button
                  type="button"
                  onClick={() => handleTabClick("commission")}
                  className={tabClass(activeTab === "commission")}
                >
                  Commission
                </button>
                <button
                  type="button"
                  onClick={() => handleTabClick("social")}
                  className={tabClass(activeTab === "social")}
                >
                  Social Options
                </button>
              </div>

              {/* Main Form Fields */}
              <form
                onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                className="flex flex-col gap-3"
              >
                {/* TAB 1: General Info */}
                {activeTab === "general" && (
                  <DetailsComponent
                    open={true}
                    hasHr={false}
                    showChevron={false}
                    className="!pt-5 sm:!pt-[25px]"
                  >
                    {/* Media Uploads: Vendor Logo & Vendor Banner */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {/* Vendor Logo Upload */}
                      <div className="w-full">
                        <label className="block text-[13px] font-medium tracking-tight mb-1">
                          Vendor Logo
                        </label>
                        <div className="flex items-center gap-3">
                          <div className="w-16 h-16 rounded-full bg-[var(--primary-bg-color)] border main-border-color flex items-center justify-center overflow-hidden shrink-0">
                            {formValues.vendor_logo || formValues.profile_picture ? (
                              <img
                                src={formValues.vendor_logo || formValues.profile_picture}
                                alt="Vendor Logo"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Camera className="w-6 h-6 text-gray-400" />
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="file"
                              ref={profileInputRef}
                              onChange={(e) => handleImageUpload(e, "vendor_logo")}
                              accept="image/*"
                              className="hidden"
                            />
                            <ButtonComponent
                              type="button"
                              variant="outline"
                              size="sm"
                              loading={uploadingField === "vendor_logo" || uploadingField === "profile_picture"}
                              icon={<Edit className="w-3.5 h-3.5" />}
                              onClick={() => profileInputRef.current?.click()}
                            >
                              {formValues.vendor_logo || formValues.profile_picture ? "Change" : "Upload"}
                            </ButtonComponent>
                            {(formValues.vendor_logo || formValues.profile_picture) && (
                              <ButtonComponent
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                  handleFieldChange("vendor_logo", "");
                                  handleFieldChange("profile_picture", "");
                                }}
                                className="!p-2"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                              </ButtonComponent>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Vendor Banner Upload */}
                      <div className="w-full">
                        <label className="block text-[13px] font-medium tracking-tight mb-1">
                          Vendor Banner
                        </label>
                        <div className="flex items-center gap-3">
                          <div className="w-28 h-16 rounded-lg bg-[var(--primary-bg-color)] border main-border-color flex items-center justify-center overflow-hidden shrink-0">
                            {formValues.vendor_banner ? (
                              <img
                                src={formValues.vendor_banner}
                                alt="Vendor Banner"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="flex flex-col items-center justify-center text-gray-400">
                                <Camera className="w-5 h-5" />
                                <span className="text-[9px] text-gray-400 mt-0.5">Banner</span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="file"
                              ref={bannerInputRef}
                              onChange={(e) => handleImageUpload(e, "vendor_banner")}
                              accept="image/*"
                              className="hidden"
                            />
                            <ButtonComponent
                              type="button"
                              variant="outline"
                              size="sm"
                              loading={uploadingField === "vendor_banner"}
                              icon={<Edit className="w-3.5 h-3.5" />}
                              onClick={() => bannerInputRef.current?.click()}
                            >
                              {formValues.vendor_banner ? "Change" : "Upload"}
                            </ButtonComponent>
                            {formValues.vendor_banner && (
                              <ButtonComponent
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => handleFieldChange("vendor_banner", "")}
                                className="!p-2"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                              </ButtonComponent>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <InputComponent
                        label="First Name"
                        name="first_name"
                        required={true}
                        value={formValues.first_name}
                        onChange={(e) => handleFieldChange("first_name", e.target.value)}
                        error={formErrors.first_name || ""}
                        placeholder="e.g. John"
                      />
                      <InputComponent
                        label="Last Name"
                        name="last_name"
                        required={true}
                        value={formValues.last_name}
                        onChange={(e) => handleFieldChange("last_name", e.target.value)}
                        error={formErrors.last_name || ""}
                        placeholder="e.g. Doe"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <InputComponent
                        label="Email Address"
                        name="email"
                        type="email"
                        required={true}
                        value={formValues.email}
                        onChange={(e) => handleFieldChange("email", e.target.value)}
                        error={formErrors.email || ""}
                        placeholder="vendor@example.com"
                      />
                      <InputComponent
                        label="Phone Number"
                        name="phone"
                        required={true}
                        value={formValues.phone}
                        onChange={(e) => handleFieldChange("phone", e.target.value)}
                        error={formErrors.phone || ""}
                        placeholder="e.g. 9876543210"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <InputComponent
                        label="Shop Name"
                        name="shop_name"
                        required={true}
                        value={formValues.shop_name}
                        onChange={handleShopNameChange}
                        error={formErrors.shop_name || ""}
                        placeholder="e.g. Acme Superstore"
                      />
                      <InputComponent
                        label="Shop Handle / Slug"
                        name="shop_slug"
                        required={true}
                        value={formValues.shop_slug}
                        onChange={(e) => handleFieldChange("shop_slug", e.target.value)}
                        error={formErrors.shop_slug || ""}
                        placeholder="acme-superstore"
                      />
                    </div>

                    <div className="mb-4">
                      <InputComponent
                        label="Vendor Tagline"
                        name="vendor_tagline"
                        value={formValues.vendor_tagline}
                        onChange={(e) => handleFieldChange("vendor_tagline", e.target.value)}
                        error={formErrors.vendor_tagline || ""}
                        placeholder="e.g. Quality products at the best prices"
                      />
                    </div>

                    <div className="mb-2">
                      <TextareaComponent
                        label="Vendor Address"
                        name="vendor_address"
                        rows={3}
                        value={formValues.vendor_address}
                        onChange={(e) => handleFieldChange("vendor_address", e.target.value)}
                        placeholder="Enter full vendor business address..."
                      />
                    </div>
                  </DetailsComponent>
                )}

                {/* TAB 2: Commission */}
                {activeTab === "commission" && (
                  <DetailsComponent
                    open={true}
                    hasHr={false}
                    showChevron={false}
                    className="!pt-5 sm:!pt-[25px]"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
                      <SelectComponent
                        label="Commission Type"
                        required={true}
                        name="commission_type"
                        value={formValues.commission_type}
                        onChange={(e) => handleFieldChange("commission_type", e.target.value)}
                        error={formErrors.commission_type || ""}
                        options={[
                          { value: "percentage", label: "Percentage (%)" },
                          { value: "fixed", label: "Fixed Rate ($)" },
                          { value: "category", label: "Category Based" },
                        ]}
                      />

                      <InputComponent
                        label={`Commission Rate (${formValues.commission_type === "percentage" ? "%" : "$"})`}
                        name="commission"
                        type="number"
                        step="0.01"
                        required={true}
                        value={formValues.commission}
                        onChange={(e) => handleFieldChange("commission", e.target.value)}
                        error={formErrors.commission || ""}
                        placeholder="0"
                      />
                    </div>
                  </DetailsComponent>
                )}

                {/* TAB 3: Social Options */}
                {activeTab === "social" && (
                  <DetailsComponent
                    open={true}
                    hasHr={false}
                    showChevron={false}
                    className="!pt-5 sm:!pt-[25px]"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
                      <InputComponent
                        label="Twitter Profile"
                        name="social_links.twitter"
                        value={formValues.social_links?.twitter || ""}
                        onChange={(e) => handleNestedChange("social_links", "twitter", e.target.value)}
                        placeholder="https://twitter.com/username"
                      />
                      <InputComponent
                        label="Facebook Profile"
                        name="social_links.facebook"
                        value={formValues.social_links?.facebook || ""}
                        onChange={(e) => handleNestedChange("social_links", "facebook", e.target.value)}
                        placeholder="https://facebook.com/username"
                      />
                      <InputComponent
                        label="Instagram Profile"
                        name="social_links.instagram"
                        value={formValues.social_links?.instagram || ""}
                        onChange={(e) => handleNestedChange("social_links", "instagram", e.target.value)}
                        placeholder="https://instagram.com/username"
                      />
                      <InputComponent
                        label="LinkedIn Profile"
                        name="social_links.linkedin"
                        value={formValues.social_links?.linkedin || ""}
                        onChange={(e) => handleNestedChange("social_links", "linkedin", e.target.value)}
                        placeholder="https://linkedin.com/in/username"
                      />
                    </div>
                  </DetailsComponent>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Bottom Action Bar */}
      <div className="flex items-center justify-between w-full border-t border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-3.5 px-6 shrink-0">
        <button
          type="button"
          onClick={handleCancelClick}
          className="flex items-center text-xs font-medium text-gray-500 dark:text-zinc-400 hover:text-gray-900 dark:hover:text-white transition-colors gap-1 cursor-pointer"
        >
          ← cancel
        </button>

        <div className="flex items-center gap-2">
          {activeTab !== "general" && (
            <ButtonComponent
              type="button"
              variant="outline"
              size="sm"
              onClick={handlePrev}
            >
              Previous
            </ButtonComponent>
          )}

          {activeTab !== "social" ? (
            <ButtonComponent
              type="button"
              variant="primary"
              size="sm"
              onClick={handleNext}
            >
              Next
            </ButtonComponent>
          ) : null}

          <ButtonComponent
            variant="primary"
            size="sm"
            loading={btnLoading}
            disabled={modifiedCount === 0 || btnLoading}
            onClick={handleBulkUpdate}
          >
            {modifiedCount > 0 ? `Save Changes (${modifiedCount})` : "Save Changes"}
          </ButtonComponent>
        </div>
      </div>

      {/* Unsaved Changes Confirmation Modal */}
      {showUnsavedModal && (
        <Dialog
          open={showUnsavedModal}
          onClose={() => setShowUnsavedModal(false)}
          sx={{ zIndex: 999999 }}
        >
          <div className="w-full secondary-bg-color sm:w-[500px] p-6 rounded-[6px]">
            <div className="flex text-primary items-center justify-between">
              <p className="text-lg font-medium">Unsaved Changes</p>
              <button
                type="button"
                onClick={() => setShowUnsavedModal(false)}
                className="close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <hr className="my-4 main-border-color" />
            <p className="text-sm table-text">
              You have unsaved changes. Do you want to leave and discard these changes, or stay on this page?
            </p>
            <div className="flex items-center gap-3 mt-4">
              <ButtonComponent
                variant="secondary"
                size="sm"
                onClick={() => {
                  setShowUnsavedModal(false);
                  bulkVendorsDataRef.current = {};
                  if (onClose) onClose();
                }}
              >
                Leave page
              </ButtonComponent>
              <ButtonComponent
                variant="primary"
                size="sm"
                onClick={() => setShowUnsavedModal(false)}
              >
                Stay on Page
              </ButtonComponent>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default BulkEditVendor;
